import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileSettingsStore } from '@/stores/profileSettingsStore';
import {
  DASHBOARD_TOUR_FORCE_START_EVENT,
  ONBOARDING_BACK_FROM_DASHBOARD_TOUR_EVENT,
  ONBOARDING_STEPS,
} from './model/constants';
import { useCenterOnboarding } from './hooks/useCenterOnboarding';
import { resolveOnboardingFlowStage } from './model/flow';
import OnboardingModal from './components/modal/OnboardingModal';
import BusinessProfileStep from './components/steps/business-profile/BusinessProfileStep';
import AudioSetupStep from './components/steps/audio-setup/AudioSetupStep';
import BusinessInfoDetailsStep from './components/steps/business-info-details/BusinessInfoDetailsStep';
import ScriptSetupStep from './components/steps/script-setup/ScriptSetupStep';

const CenterOnboardingGate: React.FC = () => {
  const { role } = useAuth();
  const { profile } = useProfileSettingsStore();
  const [forceSummary, setForceSummary] = useState(false);
  const [bypassSavedDescription, setBypassSavedDescription] = useState(false);

  useEffect(() => {
    const handleBackFromTour = () => {
      setBypassSavedDescription(false);
      setForceSummary(true);
    };
    const handleDashboardTourStart = () => setForceSummary(false);

    window.addEventListener(ONBOARDING_BACK_FROM_DASHBOARD_TOUR_EVENT, handleBackFromTour);
    window.addEventListener(DASHBOARD_TOUR_FORCE_START_EVENT, handleDashboardTourStart);

    return () => {
      window.removeEventListener(ONBOARDING_BACK_FROM_DASHBOARD_TOUR_EVENT, handleBackFromTour);
      window.removeEventListener(DASHBOARD_TOUR_FORCE_START_EVENT, handleDashboardTourStart);
    };
  }, []);

  const {
    center,
    isOpen,
    isLoading,
    isSaving,
    error,
    dismissedRouteKey,
    businessProfile,
    currentStep,
    completedSteps,
    setupStatus,
    setupLoading,
    checkSetupStatus,
    saveAndExit,
    goToStep,
    saveBusinessProfileStep,
    saveBusinessProfileDraft,
    saveScriptStep,
    saveBusinessInfoDetailsStep,
    completeAudioSetupStep,
    scriptValidation,
  } = useCenterOnboarding();
  const { i18n } = useTranslation();
  const isRTL = i18n.language.startsWith('he') || i18n.language.startsWith('ar');
  const flowStage = resolveOnboardingFlowStage({
    role,
    profile,
    center,
  });
  const shouldRenderOnboarding = Boolean(
    center &&
    dismissedRouteKey === null &&
    (forceSummary || (flowStage === 'center-onboarding' && isOpen))
  );

  if ((isLoading && !center) || !shouldRenderOnboarding) {
    return null;
  }

  if (!center) {
    return null;
  }

  const stepDefinition = ONBOARDING_STEPS.find((step) => step.step === currentStep) ?? ONBOARDING_STEPS[0];
  const stepTransition = {
    initial: { opacity: 0, scale: 0.985, y: 10 },
    animate: { opacity: 1, scale: 1, y: 0 },
    exit: { opacity: 0, scale: 0.992, y: -6 },
    transition: { duration: 0.24, ease: [0.22, 1, 0.36, 1] as const },
  };
  const figmaStepContent =
    currentStep === 1 ? (
      <AudioSetupStep
        setupStatus={setupStatus}
        setupLoading={setupLoading}
        isSaving={isSaving}
        onRefreshStatus={checkSetupStatus}
        onContinue={completeAudioSetupStep}
      />
    ) : currentStep === 2 ? (
      <BusinessProfileStep
        value={businessProfile}
        isSaving={isSaving}
        error={error}
        onContinue={async (nextProfile) => {
          setBypassSavedDescription(true);
          await saveBusinessProfileStep(nextProfile);
        }}
        onSaveAndExit={async (draft) => {
          await saveBusinessProfileDraft(draft);
          await goToStep(1);
        }}
      />
    ) : currentStep === 4 ? (
      <ScriptSetupStep
        centerId={center.id}
        canContinue={scriptValidation.isComplete}
        isSaving={isSaving}
        onBack={() => goToStep(3)}
        onContinue={saveScriptStep}
        onSaveAndExit={saveAndExit}
      />
    ) : currentStep === 3 || currentStep === 5 ? (
      <BusinessInfoDetailsStep
        businessProfile={businessProfile}
        savedDescription={bypassSavedDescription ? '' : (center.description ?? '')}
        isSaving={isSaving}
        error={error}
        onComplete={saveBusinessInfoDetailsStep}
        onBack={() => {
          setBypassSavedDescription(false);
          const prevStep = (
            completedSteps.filter((s) => s < currentStep).sort((a, b) => b - a)[0] ?? 1
          ) as typeof currentStep;
          void goToStep(prevStep);
        }}
      />
    ) : null;

  return (
    figmaStepContent ? (
      <div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4"
        dir={isRTL ? 'rtl' : 'ltr'}
        data-onboarding-open={isOpen ? 'true' : 'false'}
        data-onboarding-saving={isSaving ? 'true' : 'false'}
      >
        <div className="absolute inset-0 bg-[rgba(25,28,30,0.20)] backdrop-blur-[4px]" />
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-12 top-12 h-96 w-96 rounded-full bg-[rgba(173,51,10,0.10)] blur-[60px]" />
          <div className="absolute bottom-12 right-12 h-[480px] w-[480px] rounded-full bg-[rgba(81,95,116,0.10)] blur-[75px]" />
        </div>

        <motion.div
          key={currentStep}
          initial={stepTransition.initial}
          animate={stepTransition.animate}
          transition={stepTransition.transition}
          className="relative z-10 flex w-full justify-center"
        >
          {figmaStepContent}
        </motion.div>
      </div>
    ) : (
      <OnboardingModal
        stepDefinition={stepDefinition}
        steps={ONBOARDING_STEPS}
        currentStep={currentStep}
        completedSteps={completedSteps}
        onStepSelect={goToStep}
        onSaveAndExit={() => void saveAndExit()}
        error={error}
      >
      </OnboardingModal>
    )
  );
};

export default CenterOnboardingGate;
